/* eslint-disable @typescript-eslint/no-explicit-any */
import { Layout, FloatButton, Popover, Card } from 'antd';
import { PhoneOutlined, LoadingOutlined, MailOutlined, InboxOutlined, FacebookOutlined } from '@ant-design/icons';
import { Content, Header } from 'antd/es/layout/layout';
import Sider from 'antd/es/layout/Sider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import URL from 'constant/url';
import { ACCOUNT_TABS_LABEL, HISTORY_TABS_LABEL, PRODUCT_TYPE } from 'constant/common';
import { Helmet } from 'react-helmet-async';
import { useAppDispatch, useAppSelector } from 'redux/store';
import {
  actionGetContactInfo,
  selectContactInfo,
  selectLoadingGetContactInfo,
} from 'components/Authentication/Login/slice';
import { actionGetUserProfile } from 'components/Account/AccountProfileTab/slice';
import { actionGetAnnouncements } from 'components/Header/Notification/slice';
import HeaderLayout from './HeaderLayout';
import Sidebar from './Sidebar';

interface MainLayoutProps {
  children: JSX.Element;
}

export default function MainLayout(props: MainLayoutProps) {
  const dispatch = useAppDispatch();
  const [openContactPopOver, setOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { children } = props;
  const [breadcrumb, setBreadcrumb] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const contactInfo = useAppSelector(selectContactInfo);
  const statusGetContactInfo = useAppSelector(selectLoadingGetContactInfo);
  const { hostname } = window.location;
  const site_name = hostname.split('.')?.length >= 2 ? hostname.split('.')?.[1] : 'master';

  useEffect(() => {
    // Định kỳ 5 phút gọi lại API để kiểm tra số dư và thông báo mới
    const INTERVAL_POLLING_TIME = 5 * 60 * 1000;
    const interval = setInterval(() => {
      dispatch(actionGetUserProfile());
      dispatch(actionGetAnnouncements());
    }, INTERVAL_POLLING_TIME);

    return () => clearInterval(interval);
  }, [dispatch]);

  // set breadcrumb header follow to url param
  function handleSelectItem(data: { key: string }) {
    switch (data.key) {
      case URL.Account:
        setBreadcrumb(ACCOUNT_TABS_LABEL.UserInfo);
        break;
      case URL.History:
        setBreadcrumb(HISTORY_TABS_LABEL.TransactionHistory);
        break;
      case URL.BuyingVPSGPU:
        setBreadcrumb('Mua VPS');
        break;
      case URL.BuyingProxy:
        setBreadcrumb('Mua Proxy');
        break;
      case URL.BuyingProxyResisdent:
        setBreadcrumb('Mua Proxy Dân cư');
        break;
      case `${URL.Management}?type=VPS&by_time=using`:
        setBreadcrumb('Danh sách VPS - Đang sử dụng');
        break;
      case `${URL.Management}?type=VPS&by_time=due`:
        setBreadcrumb('Danh sách VPS - Còn hạn (dưới 3 ngày)');
        break;
      case `${URL.Management}?type=VPS&by_time=expired`:
        setBreadcrumb('Danh sách VPS - Đã hết hạn');
        break;
      case `${URL.Management}?type=VPS&by_time=all`:
        setBreadcrumb('Danh sách VPS - Tất cả dịch vụ');
        break;
      case `${URL.Management}?type=Proxy&by_time=using`:
        setBreadcrumb('Danh sách Proxy - Đang sử dụng');
        break;
      case `${URL.Management}?type=Proxy&by_time=due`:
        setBreadcrumb('Danh sách Proxy - Còn hạn (dưới 3 ngày)');
        break;
      case `${URL.Management}?type=Proxy&by_time=expired`:
        setBreadcrumb('Danh sách Proxy - Đã hết hạn');
        break;
      case `${URL.Management}?type=Proxy&by_time=all`:
        setBreadcrumb('Danh sách Proxy - Tất cả dịch vụ');
        break;
      case URL.IPManagement:
        setBreadcrumb('Quản lý IP');
        break;
      case URL.Notification:
        setBreadcrumb('Thông báo');
        break;
      default:
        break;
    }
    navigate(data.key);
  }

  // get breadcrum after reload page
  useEffect(() => {
    if (searchParams.get('type') === 'info' || window.location.pathname === URL.Account) {
      setBreadcrumb(ACCOUNT_TABS_LABEL.UserInfo);
    }
    if (searchParams.get('type') === 'change-password') {
      setBreadcrumb(ACCOUNT_TABS_LABEL.ChangePassword);
    }
    if (searchParams.get('type') === 'transaction' || window.location.pathname === URL.History) {
      setBreadcrumb(HISTORY_TABS_LABEL.TransactionHistory);
    }
    if (searchParams.get('type') === 'change-ip') {
      setBreadcrumb(HISTORY_TABS_LABEL.ChangeIPHistory);
    }
    if (searchParams.get('type') === PRODUCT_TYPE.VPS && searchParams.has('nation')) {
      setBreadcrumb('Mua VPS');
    }
    if (window.location.pathname === URL.BuyingVPSGPU) {
      setBreadcrumb('Mua VPS');
    }
    if (searchParams.get('type') === PRODUCT_TYPE.VPS && searchParams.get('by_time') === 'using') {
      setBreadcrumb('Danh sách VPS - Đang sử dụng');
    }
    if (searchParams.get('type') === PRODUCT_TYPE.VPS && searchParams.get('by_time') === 'due') {
      setBreadcrumb('Danh sách VPS - Còn hạn (dưới 3 ngày)');
    }
    if (searchParams.get('type') === PRODUCT_TYPE.VPS && searchParams.get('by_time') === 'expired') {
      setBreadcrumb('Danh sách VPS - Đã hết hạn');
    }
    if (searchParams.get('type') === PRODUCT_TYPE.VPS && searchParams.get('by_time') === 'all') {
      setBreadcrumb('Danh sách VPS - Tất cả dịch vụ');
    }
    if (window.location.pathname === URL.BuyingProxy) {
      setBreadcrumb('Mua Proxy');
    }
    if (window.location.pathname === URL.BuyingProxyResisdent) {
      setBreadcrumb('Mua Proxy Dân cư');
    }
    if (searchParams.get('type') === PRODUCT_TYPE.Proxy && searchParams.get('by_time') === 'using') {
      setBreadcrumb('Danh sách Proxy - Đang sử dụng');
    }
    if (searchParams.get('type') === PRODUCT_TYPE.Proxy && searchParams.get('by_time') === 'due') {
      setBreadcrumb('Danh sách Proxy - Còn hạn (dưới 3 ngày)');
    }
    if (searchParams.get('type') === PRODUCT_TYPE.Proxy && searchParams.get('by_time') === 'expired') {
      setBreadcrumb('Danh sách Proxy - Đã hết hạn');
    }
    if (searchParams.get('type') === PRODUCT_TYPE.Proxy && searchParams.get('by_time') === 'all') {
      setBreadcrumb('Danh sách Proxy - Tất cả dịch vụ');
    }
    if (window.location.pathname === URL.IPManagement) {
      setBreadcrumb('Quản lý IP');
    }
    if (window.location.pathname === URL.Notification) {
      setBreadcrumb('Thông báo');
    }
  }, [searchParams]);

  useEffect(() => {
    dispatch(actionGetContactInfo({ site_name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contactInfoCard = (
    <Card size="small" bordered={false}>
      {contactInfo?.find((item: any) => item.key === 'email')?.value ? (
        <div className="flex items-center space-x-2">
          <MailOutlined />
          <span>{contactInfo?.find((item: any) => item.key === 'email')?.value ?? '--'}</span>
        </div>
      ) : null}
      {contactInfo?.find((item: any) => item.key === 'telegram')?.value ? (
        <div className="flex items-center space-x-2">
          <InboxOutlined />
          <span>telegram: {contactInfo?.find((item: any) => item.key === 'telegram')?.value ?? '--'}</span>
        </div>
      ) : null}
      {contactInfo?.find((item: any) => item.key === 'phone')?.value ? (
        <div className="flex items-center space-x-2">
          <PhoneOutlined />
          <span>
            <span>{contactInfo?.find((item: any) => item.key === 'phone')?.value ?? '--'}</span>
          </span>
        </div>
      ) : null}
      {contactInfo?.find((item: any) => item.key === 'facebook')?.value ? (
        <div className="flex items-center space-x-2">
          <FacebookOutlined />
          <span>{contactInfo?.find((item: any) => item.key === 'facebook')?.value ?? '--'}</span>
        </div>
      ) : null}
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>{breadcrumb}</title>
      </Helmet>
      <Layout className="h-full">
        <Header
          className="h-auto mobile:hidden mobile:px-1 w-full pr-[50px] pl-0"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <HeaderLayout breadcrumb={breadcrumb} />
        </Header>
        <div className="w-full h-full overflow-hidden relative">
          <Layout className="h-full" hasSider>
            <Sider
              className="mobile:hidden"
              width={220}
              collapsible
              collapsed={collapsed}
              onCollapse={(value) => setCollapsed(value)}
            >
              <Sidebar handleSelectItem={handleSelectItem} />
            </Sider>
            <Layout>
              <div className="hidden mobile:block mb-2">
                <HeaderLayout />
              </div>
              <Content className="overflow-auto h-full relative">{children}</Content>
              <Popover
                content={contactInfoCard}
                title="Thông tin liên hệ"
                trigger="click"
                placement="topRight"
                open={openContactPopOver}
                onOpenChange={(newOpen) => setOpen(newOpen)}
              >
                <FloatButton
                  icon={statusGetContactInfo === 'loading' ? <LoadingOutlined /> : <PhoneOutlined />}
                  type="primary"
                  style={{ right: 42, bottom: 24 }}
                  onClick={() => setOpen(!openContactPopOver)}
                />
              </Popover>
            </Layout>
          </Layout>
        </div>
      </Layout>
    </>
  );
}
